import React, { useState, useEffect, useCallback, useRef } from 'react';
import secureStorage from '../utils/secureStorage';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  ArrowLeft,
  ChevronRight,
  Edit,
  GripVertical,
  Loader2,
  Package,
  PanelRightClose,
  PanelRightOpen,
  Plane,
  Plus,
  Printer,
  Save,
  Search,
  Shield,
  Trash2,
  UserMinus,
  UserPlus,
  Users,
  X
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import { writeSanitizedHTML } from '../utils/safePrint';

const getAuthHeaders = () => {
  const token = secureStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const emptyTeam = {
  name: '', kind_and_type: '', current_status: 'Available', assignment: '',
  leader_name: '', leader_contact: '',
  personnel_roster: [],
  equipment_inventory: [],
  radio_frequency: '', call_sign: '', phone_number: '',
  expected_arrival_time: '', check_in_location: '', deployment_instructions: '',
  operational_period_start: '', operational_period_end: '',
  fuel_requirements: '', maintenance_capabilities: '', lodging_needs: '',
  agency_accounting_code: '', check_in_time: '', check_out_time: '', safety_injuries: ''
};

const statusBadgeClass = (status) => {
  const map = { 'Assigned': 'bg-blue-500/20 text-blue-400', 'In Transit': 'bg-yellow-500/20 text-yellow-400', 'Released': 'bg-green-500/20 text-green-400' };
  return map[status] || 'bg-gray-500/20 text-gray-400';
};

const StrikeTeamsModule = ({ currentExercise, theme }) => {
  const API = API_BASE_URL + '/api';
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({ ...emptyTeam });
  const [participants, setParticipants] = useState([]);
  const [resources, setResources] = useState([]);
  const [aircraft, setAircraft] = useState([]); // Flattened Fixed Wing / Helicopters / Drones from ICS 220
  const [saving, setSaving] = useState(false);
  const [newEquipment, setNewEquipment] = useState({ description: '', identification_number: '', capability: '' });

  // Quick Assign state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('participants');
  const [searchQuery, setSearchQuery] = useState('');
  const [dropTargetId, setDropTargetId] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [toast, setToast] = useState(null);

  const toastTimer = useRef(null);
  const showToast = (message, type = 'success') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };

  const fetchTeams = useCallback(async () => {
    if (!currentExercise?.id) return;
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/strike-teams?exercise_id=${currentExercise.id}`, { headers });
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching strike teams:', error);
    } finally {
      setLoading(false);
    }
  }, [currentExercise?.id, API]);

  const fetchParticipants = useCallback(async () => {
    if (!currentExercise?.id) return;
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/exercises/${currentExercise.id}/participants`, { headers });
      setParticipants(response.data || []);
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  }, [currentExercise?.id, API]);

  const fetchResources = useCallback(async () => {
    if (!currentExercise?.id) return;
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/resources?exercise_id=${currentExercise.id}`, { headers });
      const allResources = response.data || [];
      // Fetch individual units for all resources. If a resource has zero units,
      // fall back to representing the parent resource itself so it still appears
      // in Quick Assign (assignments then route to /resources/{id}/assign).
      const allItems = [];
      for (const res of allResources) {
        try {
          const unitsRes = await axios.get(`${API}/resources/${res.id}/units`, { headers });
          const units = unitsRes.data || [];
          if (units.length > 0) {
            units.forEach(u => allItems.push({ ...u, resource_type: res.resource_type, parent_resource_id: res.id, is_parent: false }));
          } else {
            // Parent-only fallback (no child units defined yet)
            allItems.push({
              id: res.id,
              unit_name: res.resource_type || res.identification || 'Unnamed Resource',
              resource_type: res.resource_type,
              status: res.status || 'Available',
              strike_team: res.strike_team || '',
              parent_resource_id: res.id,
              is_parent: true,
            });
          }
        } catch (unitsErr) {
          console.warn(`[StrikeTeamsModule] units fetch failed for resource ${res.id}:`, unitsErr?.message || unitsErr);
        }
      }
      setResources(allItems);
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  }, [currentExercise?.id, API]);

  const fetchAircraft = useCallback(async () => {
    if (!currentExercise?.id) return;
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/ics-220?exercise_id=${currentExercise.id}`, { headers });
      const summaries = response.data || [];
      const flat = [];
      summaries.forEach(s => {
        (s.fixed_wing_aircraft || []).forEach(a => flat.push({ ...a, summary_id: s.id, category: 'fixed_wing' }));
        (s.helicopters || []).forEach(a => flat.push({ ...a, summary_id: s.id, category: 'helicopter' }));
        (s.drones || []).forEach(a => flat.push({ ...a, summary_id: s.id, category: 'drone' }));
      });
      setAircraft(flat);
    } catch (error) {
      console.error('Error fetching ICS 220 aircraft:', error);
    }
  }, [currentExercise?.id, API]);

  useEffect(() => {
    fetchTeams();
    fetchParticipants();
    fetchResources();
    fetchAircraft();
  }, [fetchTeams, fetchParticipants, fetchResources, fetchAircraft]);

  // Quick Assign - Participant
  const assignParticipant = async (participantId, teamName) => {
    setAssigningId(participantId);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      await axios.patch(`${API}/participants/${participantId}/assign`, { strikeTeam: teamName }, { headers });
      setParticipants(prev => prev.map(p => p.id === participantId ? { ...p, strikeTeam: teamName } : p));
      showToast(`Assigned to ${teamName}`);
    } catch (error) {
      showToast('Failed to assign', 'error');
    } finally {
      setAssigningId(null);
      setDropTargetId(null);
    }
  };

  const unassignParticipant = async (participantId) => {
    setAssigningId(participantId);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      await axios.patch(`${API}/participants/${participantId}/assign`, { strikeTeam: '' }, { headers });
      setParticipants(prev => prev.map(p => p.id === participantId ? { ...p, strikeTeam: '' } : p));
      showToast('Unassigned from strike team');
    } catch (error) {
      showToast('Failed to unassign', 'error');
    } finally {
      setAssigningId(null);
    }
  };

  // Quick Assign - Resource
  const assignResource = async (resourceId, teamName) => {
    setAssigningId(resourceId);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const item = resources.find(r => r.id === resourceId);
      const endpoint = item?.is_parent
        ? `${API}/resources/${resourceId}/assign`
        : `${API}/resource-units/${resourceId}/assign`;
      await axios.patch(endpoint, { strike_team: teamName }, { headers });
      setResources(prev => prev.map(r => r.id === resourceId ? { ...r, strike_team: teamName } : r));
      showToast(item?.is_parent ? `Resource assigned to ${teamName}` : `Unit assigned to ${teamName}`);
    } catch (error) {
      showToast('Failed to assign resource', 'error');
    } finally {
      setAssigningId(null);
      setDropTargetId(null);
    }
  };

  const unassignResource = async (resourceId) => {
    setAssigningId(resourceId);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const item = resources.find(r => r.id === resourceId);
      const endpoint = item?.is_parent
        ? `${API}/resources/${resourceId}/assign`
        : `${API}/resource-units/${resourceId}/assign`;
      await axios.patch(endpoint, { strike_team: '' }, { headers });
      setResources(prev => prev.map(r => r.id === resourceId ? { ...r, strike_team: '' } : r));
      showToast('Resource unassigned');
    } catch (error) {
      showToast('Failed to unassign', 'error');
    } finally {
      setAssigningId(null);
    }
  };

  // Quick Assign - Aircraft (fixed wing / helicopter / drone from ICS 220)
  const aircraftKey = (a) => `air-${a.summary_id}-${a.category}-${a.id}`;

  const assignAircraft = async (aircraftItem, teamName) => {
    const key = aircraftKey(aircraftItem);
    setAssigningId(key);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      await axios.patch(
        `${API}/ics-220/${aircraftItem.summary_id}/aircraft/${aircraftItem.id}/assign`,
        { category: aircraftItem.category, strike_team: teamName },
        { headers }
      );
      setAircraft(prev => prev.map(a =>
        (a.summary_id === aircraftItem.summary_id && String(a.id) === String(aircraftItem.id) && a.category === aircraftItem.category)
          ? { ...a, strike_team: teamName } : a
      ));
      showToast(`Aircraft assigned to ${teamName}`);
    } catch (error) {
      showToast('Failed to assign aircraft', 'error');
    } finally {
      setAssigningId(null);
      setDropTargetId(null);
    }
  };

  const unassignAircraft = async (aircraftItem) => {
    const key = aircraftKey(aircraftItem);
    setAssigningId(key);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      await axios.patch(
        `${API}/ics-220/${aircraftItem.summary_id}/aircraft/${aircraftItem.id}/assign`,
        { category: aircraftItem.category, strike_team: '' },
        { headers }
      );
      setAircraft(prev => prev.map(a =>
        (a.summary_id === aircraftItem.summary_id && String(a.id) === String(aircraftItem.id) && a.category === aircraftItem.category)
          ? { ...a, strike_team: '' } : a
      ));
      showToast('Aircraft unassigned');
    } catch (error) {
      showToast('Failed to unassign', 'error');
    } finally {
      setAssigningId(null);
    }
  };

  // Drag handlers
  const onDragStart = (e, item, itemType) => {
    if (itemType === 'aircraft') {
      e.dataTransfer.setData('item_id', JSON.stringify({ summary_id: item.summary_id, aircraft_id: item.id, category: item.category }));
    } else {
      e.dataTransfer.setData('item_id', item.id);
    }
    e.dataTransfer.setData('item_type', itemType);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, teamId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetId(teamId);
  };

  const onDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDropTargetId(null);
  };

  const onDrop = (e, teamName) => {
    e.preventDefault();
    const itemId = e.dataTransfer.getData('item_id');
    const itemType = e.dataTransfer.getData('item_type');
    if (itemId) {
      if (itemType === 'resource') {
        assignResource(itemId, teamName);
      } else if (itemType === 'aircraft') {
        try {
          const { summary_id, aircraft_id, category } = JSON.parse(itemId);
          const aircraftItem = aircraft.find(a => a.summary_id === summary_id && String(a.id) === String(aircraft_id) && a.category === category);
          if (aircraftItem) assignAircraft(aircraftItem, teamName);
        } catch (err) { console.error('Bad aircraft drag payload', err); }
      } else {
        assignParticipant(itemId, teamName);
      }
    }
    setDropTargetId(null);
  };

  // CRUD handlers
  const handleSave = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const payload = { ...formData, exercise_id: currentExercise.id };
      if (editingTeam) {
        await axios.put(`${API}/strike-teams/${editingTeam.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/strike-teams`, payload, { headers });
      }
      setView('list');
      setEditingTeam(null);
      setFormData({ ...emptyTeam });
      fetchTeams();
    } catch (error) {
      console.error('Error saving strike team:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm('Delete this strike team?')) return;
    try {
      await axios.delete(`${API}/strike-teams/${teamId}`, { headers: getAuthHeaders() });
      fetchTeams();
    } catch (error) {
      console.error('Error deleting strike team:', error);
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setFormData({
      ...emptyTeam, ...team,
      personnel_roster: team.personnel_roster || [],
      equipment_inventory: team.equipment_inventory || []
    });
    setView('form');
  };

  const handleNew = () => {
    setEditingTeam(null);
    setFormData({ ...emptyTeam });
    setView('form');
  };

  const addEquipment = () => {
    if (!newEquipment.description.trim()) return;
    setFormData(prev => ({
      ...prev,
      equipment_inventory: [...prev.equipment_inventory, { ...newEquipment }]
    }));
    setNewEquipment({ description: '', identification_number: '', capability: '' });
  };

  const removeEquipment = (idx) => {
    setFormData(prev => ({
      ...prev,
      equipment_inventory: prev.equipment_inventory.filter((_, i) => i !== idx)
    }));
  };

  const matchingParticipants = participants.filter(p =>
    p.strikeTeam === formData.name || p.strike_team === formData.name
  );

  const matchingResources = resources.filter(r => r.strike_team === formData.name);

  const handlePrint = (team) => {
    const teamResources = resources.filter(r => r.strike_team === team.name);
    const equipmentRows = (team.equipment_inventory || []).map(e =>
      `<tr><td style="border:1px solid #ddd;padding:8px;">${e.description}</td><td style="border:1px solid #ddd;padding:8px;">${e.identification_number}</td><td style="border:1px solid #ddd;padding:8px;">${e.capability}</td></tr>`
    ).join('');
    const resourceRows = teamResources.map(r =>
      `<tr><td style="border:1px solid #ddd;padding:8px;">${r.unit_name || ''}</td><td style="border:1px solid #ddd;padding:8px;">${r.resource_type || ''}</td><td style="border:1px solid #ddd;padding:8px;">${r.status || ''}</td><td style="border:1px solid #ddd;padding:8px;">${r.personnel_needed || 0}</td><td style="border:1px solid #ddd;padding:8px;">${r.location || ''}</td></tr>`
    ).join('');

    const printContent = `
      <html><head><title>Strike Team - ${team.name}</title>
      <style>
        body{font-family:Arial,sans-serif;color:#333;padding:20px;line-height:1.5}
        h1{color:#ea580c;border-bottom:3px solid #ea580c;padding-bottom:8px;margin-bottom:5px}
        h2{color:#ea580c;margin-top:20px;font-size:16px;border-bottom:1px solid #ea580c;padding-bottom:4px}
        table{width:100%;border-collapse:collapse;margin:8px 0}
        th{background:#f97316;color:white;border:1px solid #ddd;padding:8px;text-align:left;font-size:12px}
        td{border:1px solid #ddd;padding:8px;font-size:12px}
        tr:nth-child(even){background:#f9f9f9}
        .field{margin:4px 0;font-size:13px}
        .label{font-weight:bold;display:inline-block;min-width:220px;color:#555}
        .section{margin-bottom:16px;padding:14px;border:1px solid #ddd;border-radius:6px}
        .footer{margin-top:25px;text-align:center;font-size:11px;color:#888;border-top:2px solid #ddd;padding-top:10px}
        @media print{body{margin:0;padding:10px}}
      </style></head><body>
      <h1>Strike Team: ${team.name}</h1>
      <p><strong>Exercise:</strong> ${currentExercise?.exercise_name || 'N/A'} &nbsp;|&nbsp; <strong>Generated:</strong> ${new Date().toLocaleString()}</p>

      <div class="section"><h2>1. Identification and Status</h2>
        <div class="field"><span class="label">Strike Team Name:</span> ${team.name || 'N/A'}</div>
        <div class="field"><span class="label">Kind and Type:</span> ${team.kind_and_type || 'N/A'}</div>
        <div class="field"><span class="label">Current Status:</span> ${team.current_status || 'N/A'}</div>
        <div class="field"><span class="label">Assignment:</span> ${team.assignment || 'N/A'}</div>
      </div>

      <div class="section"><h2>2. Personnel and Unit Details (Accountability)</h2>
        <div class="field">Assigned participants and resources are managed from the exercise-level Strike Team assignments and appear in the dashboard's accountability table.</div>
      </div>

      <div class="section"><h2>3. Equipment and Resources</h2>
        ${(team.equipment_inventory || []).length > 0 ? `
          <table>
            <tr><th>Description</th><th>ID Number</th><th>Capability</th></tr>
            ${equipmentRows}
          </table>
        ` : '<p style="color:#999;font-style:italic;text-align:center;padding:10px;">No equipment listed.</p>'}
      </div>

      <div class="section"><h2>4. Assigned Individual Units (${teamResources.length})</h2>
        ${teamResources.length > 0 ? `
          <table>
            <tr><th>Unit Name/ID</th><th>Resource Type</th><th>Status</th><th># of Personnel</th><th>Location</th></tr>
            ${resourceRows}
          </table>
        ` : '<p style="color:#999;font-style:italic;text-align:center;padding:10px;">No individual units assigned.</p>'}
      </div>

      <div class="section"><h2>5. Logistics and Deployment</h2>
        <div class="field"><span class="label">Radio Frequency:</span> ${team.radio_frequency || 'N/A'}</div>
        <div class="field"><span class="label">Call Sign:</span> ${team.call_sign || 'N/A'}</div>
        <div class="field"><span class="label">Phone Number:</span> ${team.phone_number || 'N/A'}</div>
        <div class="field"><span class="label">Expected Arrival:</span> ${team.expected_arrival_time || 'N/A'}</div>
        <div class="field"><span class="label">Check-in Location:</span> ${team.check_in_location || 'N/A'}</div>
        <div class="field"><span class="label">Deployment Instructions:</span> ${team.deployment_instructions || 'N/A'}</div>
        <div class="field"><span class="label">Operational Period:</span> ${team.operational_period_start || ''} - ${team.operational_period_end || ''}</div>
        <div class="field"><span class="label">Fuel Requirements:</span> ${team.fuel_requirements || 'N/A'}</div>
        <div class="field"><span class="label">Maintenance:</span> ${team.maintenance_capabilities || 'N/A'}</div>
        <div class="field"><span class="label">Lodging Needs:</span> ${team.lodging_needs || 'N/A'}</div>
      </div>

      <div class="section"><h2>6. Administrative and Finance (Accountability)</h2>
        <div class="field"><span class="label">Agency Accounting Code:</span> ${team.agency_accounting_code || 'N/A'}</div>
        <div class="field"><span class="label">Check-in Time:</span> ${team.check_in_time || 'N/A'}</div>
        <div class="field"><span class="label">Check-out Time:</span> ${team.check_out_time || 'N/A'}</div>
        <div class="field"><span class="label">Safety/Injuries:</span> ${team.safety_injuries || 'N/A'}</div>
      </div>

      <div class="footer">
        <p>Strike Team: ${team.name} | Equipment: ${(team.equipment_inventory || []).length} | Units: ${teamResources.length}</p>
        <p>Powered by EXRSIM - Emergency Response Simulation Platform</p>
      </div>
      </body></html>`;

    const w = window.open('', '_blank');
    writeSanitizedHTML(w.document, printContent);
    w.print();
  };

  const statusColors = {
    'Available': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Assigned': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'Out of Service': 'bg-red-500/20 text-red-400 border-red-500/30',
    'In Transit': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
  };

  // Sidebar data - Participants (show ALL exercise participants; those already
  // assigned to a strike team display a badge but can be reassigned via drag/drop or click).
  // Normalize sentinel values ('none', 'None', 'N/A') as unassigned.
  const isRealAssignment = (v) => {
    const s = (v || '').toString().trim().toLowerCase();
    return s !== '' && s !== 'none' && s !== 'n/a';
  };
  const unassignedParticipants = participants;
  const assignedParticipants = participants.filter(p => isRealAssignment(p.strikeTeam) || isRealAssignment(p.strike_team));
  const filteredUnassignedParticipants = unassignedParticipants.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const name = (p.name || `${p.firstName || ''} ${p.lastName || ''}`).toLowerCase();
    const org = (p.organization || '').toLowerCase();
    return name.includes(q) || org.includes(q);
  });

  // Sidebar data - Resources (show ALL exercise resources; dim already-assigned)
  const unassignedResources = resources;
  const assignedResources = resources.filter(r => isRealAssignment(r.strike_team));
  const filteredUnassignedResources = unassignedResources.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const name = (r.unit_name || '').toLowerCase();
    const type = (r.resource_type || '').toLowerCase();
    return name.includes(q) || type.includes(q);
  });

  // Sidebar data - Aircraft (from ICS 220: Fixed Wing / Helicopters / Drones)
  const assignedAircraft = aircraft.filter(a => isRealAssignment(a.strike_team));
  const filteredAircraft = aircraft.filter(a => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (a.registration || '').toLowerCase().includes(q) ||
           (a.make_model || '').toLowerCase().includes(q) ||
           (a.base || '').toLowerCase().includes(q) ||
           (a.category || '').toLowerCase().includes(q);
  });

  const hasItems = teams.length > 0 && (participants.length > 0 || resources.length > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // FORM VIEW
  if (view === 'form') {
    return (
      <div className="flex flex-col h-full">
        {/* Sticky top bar */}
        <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur-sm border-b border-gray-700 px-6 py-3 flex items-center justify-between">
          <Button variant="ghost" onClick={() => { setView('list'); setEditingTeam(null); }} className="text-orange-500 hover:text-orange-400">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Strike Teams
          </Button>
          <div className="flex items-center gap-3">
            <Button variant="outline" className="border-gray-600 text-gray-400" onClick={() => { setView('list'); setEditingTeam(null); }}>Cancel</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-black font-semibold" onClick={handleSave} disabled={saving || !formData.name.trim()} data-testid="save-strike-team-top-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {editingTeam ? 'Update Strike Team' : 'Create Strike Team'}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full space-y-6 pb-24">
        <h2 className="text-2xl font-bold text-white">{editingTeam ? 'Edit Strike Team' : 'Create Strike Team'}</h2>

        {/* Section 1: Identification and Status */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader><CardTitle className="text-orange-500 text-lg">1. Identification and Status</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Strike Team Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" placeholder="e.g., ST Alpha - Barrie Engines" data-testid="st-name" />
              </div>
              <div>
                <Label className="text-gray-300">Kind and Type</Label>
                <Input value={formData.kind_and_type} onChange={(e) => setFormData(prev => ({ ...prev, kind_and_type: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" placeholder="e.g., Type 1 Structural Engines" data-testid="st-kind-type" />
              </div>
              <div>
                <Label className="text-gray-300">Current Status</Label>
                <Select value={formData.current_status} onValueChange={(v) => setFormData(prev => ({ ...prev, current_status: v }))}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="st-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    {['Available', 'Assigned', 'Out of Service', 'In Transit'].map(s => (
                      <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Assignment</Label>
                <Input value={formData.assignment} onChange={(e) => setFormData(prev => ({ ...prev, assignment: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" placeholder="Division, Group, or Branch" data-testid="st-assignment" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Personnel and Unit Details (Accountability) */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader><CardTitle className="text-orange-500 text-lg">2. Personnel and Unit Details (Accountability)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {(() => {
              const stParticipants = participants.filter(p => p.strikeTeam === formData.name || p.strike_team === formData.name);
              return (
                <>
                  {stParticipants.length > 0 ? (
                    <div>
                      <Label className="text-gray-300 mb-2 block">Quick-Assigned Participants</Label>
                      <div className="overflow-x-auto mb-3">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-gray-600">
                            <th className="text-left text-gray-400 py-2 px-3">Name</th>
                            <th className="text-left text-gray-400 py-2 px-3">Organization</th>
                            <th className="text-left text-gray-400 py-2 px-3">Qualifications</th>
                          </tr></thead>
                          <tbody>
                            {stParticipants.map((p, idx) => (
                              <tr key={`st-p-${idx}`} className="border-b border-gray-700/50">
                                <td className="py-2 px-3 text-white">{p.name || ''}</td>
                                <td className="py-2 px-3 text-gray-300">{p.organization || ''}</td>
                                <td className="py-2 px-3 text-gray-300">{p.qualifications || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm italic">No participants assigned to this Strike Team yet.</p>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>

        {/* Section 3: Equipment and Resources */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader><CardTitle className="text-orange-500 text-lg">3. Equipment and Resources</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {formData.equipment_inventory.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-600">
                    <th className="text-left text-gray-400 py-2 px-3">Description</th>
                    <th className="text-left text-gray-400 py-2 px-3">ID Number</th>
                    <th className="text-left text-gray-400 py-2 px-3">Capability</th>
                    <th className="text-right text-gray-400 py-2 px-3">Action</th>
                  </tr></thead>
                  <tbody>
                    {formData.equipment_inventory.map((eq, idx) => (
                      <tr key={`eq-${idx}-${eq.description}`} className="border-b border-gray-700/50">
                        <td className="py-2 px-3 text-white">{eq.description}</td>
                        <td className="py-2 px-3 text-gray-300">{eq.identification_number}</td>
                        <td className="py-2 px-3 text-gray-300">{eq.capability}</td>
                        <td className="py-2 px-3 text-right">
                          <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-7 w-7 p-0" onClick={() => removeEquipment(idx)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <Label className="text-gray-300 text-xs">Description</Label>
                <Input value={newEquipment.description} onChange={(e) => setNewEquipment(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white text-sm" placeholder="e.g., Engine" />
              </div>
              <div>
                <Label className="text-gray-300 text-xs">ID Number</Label>
                <Input value={newEquipment.identification_number} onChange={(e) => setNewEquipment(prev => ({ ...prev, identification_number: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white text-sm" placeholder="License/Fleet ID" />
              </div>
              <div>
                <Label className="text-gray-300 text-xs">Capability</Label>
                <Input value={newEquipment.capability} onChange={(e) => setNewEquipment(prev => ({ ...prev, capability: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white text-sm" placeholder="e.g., Foam capacity" />
              </div>
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-black font-semibold" onClick={addEquipment} disabled={!newEquipment.description.trim()} data-testid="add-equipment-btn">
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Logistics and Deployment */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader><CardTitle className="text-orange-500 text-lg">4. Logistics and Deployment</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label className="text-gray-300">Radio Frequency</Label><Input value={formData.radio_frequency} onChange={(e) => setFormData(prev => ({ ...prev, radio_frequency: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" placeholder="e.g., 154.280 MHz" /></div>
              <div><Label className="text-gray-300">Call Sign</Label><Input value={formData.call_sign} onChange={(e) => setFormData(prev => ({ ...prev, call_sign: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" placeholder="e.g., Alpha-1" /></div>
              <div><Label className="text-gray-300">Phone Number</Label><Input value={formData.phone_number} onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="text-gray-300">Expected Arrival Time</Label><Input type="datetime-local" value={formData.expected_arrival_time} onChange={(e) => setFormData(prev => ({ ...prev, expected_arrival_time: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" /></div>
              <div><Label className="text-gray-300">Check-in Location</Label><Input value={formData.check_in_location} onChange={(e) => setFormData(prev => ({ ...prev, check_in_location: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" /></div>
            </div>
            <div><Label className="text-gray-300">Deployment Instructions</Label><Textarea value={formData.deployment_instructions} onChange={(e) => setFormData(prev => ({ ...prev, deployment_instructions: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" rows={2} /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="text-gray-300">Operational Period Start</Label><Input type="datetime-local" value={formData.operational_period_start} onChange={(e) => setFormData(prev => ({ ...prev, operational_period_start: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" /></div>
              <div><Label className="text-gray-300">Operational Period End</Label><Input type="datetime-local" value={formData.operational_period_end} onChange={(e) => setFormData(prev => ({ ...prev, operational_period_end: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label className="text-gray-300">Fuel Requirements</Label><Input value={formData.fuel_requirements} onChange={(e) => setFormData(prev => ({ ...prev, fuel_requirements: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" /></div>
              <div><Label className="text-gray-300">Maintenance Capabilities</Label><Input value={formData.maintenance_capabilities} onChange={(e) => setFormData(prev => ({ ...prev, maintenance_capabilities: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" /></div>
              <div><Label className="text-gray-300">Lodging Needs</Label><Input value={formData.lodging_needs} onChange={(e) => setFormData(prev => ({ ...prev, lodging_needs: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" /></div>
            </div>
          </CardContent>
        </Card>

        {/* Section 5: Administrative and Finance */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader><CardTitle className="text-orange-500 text-lg">5. Administrative and Finance (Accountability)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label className="text-gray-300">Agency Accounting Code</Label><Input value={formData.agency_accounting_code} onChange={(e) => setFormData(prev => ({ ...prev, agency_accounting_code: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" /></div>
              <div><Label className="text-gray-300">Check-in Time</Label><Input type="datetime-local" value={formData.check_in_time} onChange={(e) => setFormData(prev => ({ ...prev, check_in_time: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" /></div>
              <div><Label className="text-gray-300">Check-out Time</Label><Input type="datetime-local" value={formData.check_out_time} onChange={(e) => setFormData(prev => ({ ...prev, check_out_time: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" /></div>
            </div>
            <div><Label className="text-gray-300">Safety/Injuries</Label><Textarea value={formData.safety_injuries} onChange={(e) => setFormData(prev => ({ ...prev, safety_injuries: e.target.value }))} className="bg-gray-700 border-gray-600 text-white" rows={2} /></div>
          </CardContent>
        </Card>

        </div>

        {/* Sticky bottom save bar */}
        <div className="sticky bottom-0 z-10 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 px-6 py-3 flex justify-end gap-3">
          <Button variant="outline" className="border-gray-600 text-gray-400" onClick={() => { setView('list'); setEditingTeam(null); }}>Cancel</Button>
          <Button className="bg-orange-500 hover:bg-orange-600 text-black font-semibold" onClick={handleSave} disabled={saving || !formData.name.trim()} data-testid="save-strike-team-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {editingTeam ? 'Update Strike Team' : 'Create Strike Team'}
          </Button>
        </div>
      </div>
    );
  }

  // LIST VIEW with Quick Assign sidebar
  return (
    <div className="relative flex h-full">
      {toast && (
        <div className={`fixed top-4 z-50 px-4 py-3 rounded-lg shadow-lg border backdrop-blur-sm transition-all animate-in slide-in-from-top-2 ${sidebarOpen ? 'right-[21rem]' : 'right-4'} ${
          toast.type === 'error' ? 'bg-red-900/90 border-red-700 text-red-100' : 'bg-green-900/90 border-green-700 text-green-100'
        }`} data-testid="assign-toast">
          <div className="flex items-center gap-2">
            {toast.type === 'error' ? <X className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            <span className="text-sm font-medium">{toast.message}</span>
          </div>
        </div>
      )}

      <div className={`flex-1 p-6 space-y-6 transition-all duration-300 ${sidebarOpen ? 'mr-80' : ''}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Shield className="h-6 w-6 text-orange-500" />
              Strike Teams
            </h2>
            <p className="text-gray-400 text-sm mt-1">{currentExercise?.exercise_name || 'No exercise selected'}</p>
          </div>
          <div className="flex items-center gap-2">
            {hasItems && (
              <Button variant="outline" className={`border-orange-500/40 text-orange-400 hover:bg-orange-500/10 ${sidebarOpen ? 'bg-orange-500/10' : ''}`}
                onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="toggle-quick-assign-btn">
                {sidebarOpen ? <PanelRightClose className="h-4 w-4 mr-2" /> : <PanelRightOpen className="h-4 w-4 mr-2" />}
                Quick Assign
              </Button>
            )}
            <Button className="bg-orange-500 hover:bg-orange-600 text-black font-semibold" onClick={handleNew} data-testid="new-strike-team-btn">
              <Plus className="h-4 w-4 mr-2" /> New Strike Team
            </Button>
          </div>
        </div>

        {teams.length === 0 ? (
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="text-center py-12">
              <Shield className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-300 mb-2">No Strike Teams Created</h3>
              <p className="text-gray-500 text-sm mb-4">Create your first strike team to manage grouped resources for this exercise.</p>
              <Button className="bg-orange-500 hover:bg-orange-600 text-black font-semibold" onClick={handleNew}>
                <Plus className="h-4 w-4 mr-2" /> Create Strike Team
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {teams.map(team => {
              const teamMembers = participants.filter(p => p.strikeTeam === team.name || p.strike_team === team.name);
              const teamResources = resources.filter(r => r.strike_team === team.name);
              const teamAircraft = aircraft.filter(a => a.strike_team === team.name);
              const isDropTarget = dropTargetId === team.id;
              return (
                <Card key={team.id}
                  className={`bg-gray-800/50 border-gray-700 transition-all duration-200 ${isDropTarget ? 'border-orange-500 ring-2 ring-orange-500/30 scale-[1.01]' : 'hover:border-orange-500/40'}`}
                  data-testid={`strike-team-card-${team.id}`}
                  onDragOver={(e) => onDragOver(e, team.id)} onDragLeave={onDragLeave} onDrop={(e) => onDrop(e, team.name)}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{team.name}</h3>
                        <p className="text-gray-400 text-sm">{team.kind_and_type || 'No type specified'}</p>
                      </div>
                      <Badge className={`${statusColors[team.current_status] || 'bg-gray-500/20 text-gray-400'} border text-xs`}>{team.current_status}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-y-2 text-sm mb-3">
                      <div><span className="text-gray-500">Leader:</span> <span className="text-gray-300 ml-1">{team.leader_name || 'N/A'}</span></div>
                      <div><span className="text-gray-500">Assignment:</span> <span className="text-gray-300 ml-1">{team.assignment || 'N/A'}</span></div>
                    </div>

                    {/* Quick-glance count badges */}
                    <div className="flex flex-wrap gap-2 mb-3" data-testid={`st-badges-${team.id}`}>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
                        teamMembers.length > 0 ? 'bg-orange-500/15 border-orange-500/30 text-orange-300' : 'bg-gray-800/50 border-gray-700 text-gray-500'
                      }`} data-testid={`st-personnel-badge-${team.id}`}>
                        <Users className="h-3 w-3" />
                        {teamMembers.length} Personnel
                      </span>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
                        teamResources.length > 0 ? 'bg-teal-500/15 border-teal-500/30 text-teal-300' : 'bg-gray-800/50 border-gray-700 text-gray-500'
                      }`} data-testid={`st-resources-badge-${team.id}`}>
                        <Package className="h-3 w-3" />
                        {teamResources.length} Resource{teamResources.length !== 1 ? 's' : ''}
                      </span>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
                        teamAircraft.length > 0 ? 'bg-sky-500/15 border-sky-500/30 text-sky-300' : 'bg-gray-800/50 border-gray-700 text-gray-500'
                      }`} data-testid={`st-aircraft-badge-${team.id}`}>
                        <Plane className="h-3 w-3" />
                        {teamAircraft.length} Aircraft
                      </span>
                    </div>

                    {teamMembers.length > 0 && (
                      <div className="mb-2 border border-gray-700/50 rounded-lg p-2 bg-gray-900/30">
                        <p className="text-gray-500 text-xs mb-1.5 font-medium uppercase tracking-wider flex items-center gap-1"><Users className="h-3 w-3" /> Assigned Personnel</p>
                        <div className="flex flex-wrap gap-1.5">
                          {teamMembers.map(p => (
                            <span key={p.id} className="inline-flex items-center gap-1 bg-gray-700/60 text-gray-200 text-xs px-2 py-1 rounded-md" data-testid={`assigned-member-${p.id}`}>
                              {p.name || `${p.firstName || ''} ${p.lastName || ''}`}
                              <button className="ml-0.5 text-gray-500 hover:text-red-400 transition-colors" onClick={() => unassignParticipant(p.id)} data-testid={`unassign-${p.id}`}><X className="h-3 w-3" /></button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {teamResources.length > 0 && (
                      <div className="mb-2 border border-teal-700/30 rounded-lg p-2 bg-teal-900/10">
                        <p className="text-teal-500/70 text-xs mb-1.5 font-medium uppercase tracking-wider flex items-center gap-1"><Package className="h-3 w-3" /> Assigned Resources</p>
                        <div className="flex flex-wrap gap-1.5">
                          {teamResources.map(r => (
                            <span key={r.id} className="inline-flex items-center gap-1 bg-teal-800/30 text-teal-200 text-xs px-2 py-1 rounded-md border border-teal-700/30" data-testid={`st-assigned-resource-${r.id}`}>
                              <Package className="h-3 w-3 text-teal-400/60" />
                              {r.unit_name || r.resource_type || 'Unit'}
                              <button className="ml-0.5 text-teal-500/50 hover:text-red-400 transition-colors" onClick={() => unassignResource(r.id)} data-testid={`st-unassign-resource-${r.id}`}><X className="h-3 w-3" /></button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {teamAircraft.length > 0 && (
                      <div className="mb-2 border border-sky-700/30 rounded-lg p-2 bg-sky-900/10">
                        <p className="text-sky-500/70 text-xs mb-1.5 font-medium uppercase tracking-wider flex items-center gap-1"><Plane className="h-3 w-3" /> Assigned Aircraft</p>
                        <div className="flex flex-wrap gap-1.5">
                          {teamAircraft.map(a => (
                            <span key={aircraftKey(a)} className="inline-flex items-center gap-1 bg-sky-800/30 text-sky-200 text-xs px-2 py-1 rounded-md border border-sky-700/30" data-testid={`st-assigned-aircraft-${aircraftKey(a)}`}>
                              <Plane className="h-3 w-3 text-sky-400/60" />
                              {a.registration || a.make_model || 'Aircraft'}
                              <button className="ml-0.5 text-sky-500/50 hover:text-red-400 transition-colors" onClick={() => unassignAircraft(a)} data-testid={`st-unassign-aircraft-${aircraftKey(a)}`}><X className="h-3 w-3" /></button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {sidebarOpen && isDropTarget && (
                      <div className="mb-3 border-2 border-dashed border-orange-500/50 rounded-lg p-3 bg-orange-500/5 text-center">
                        <UserPlus className="h-5 w-5 text-orange-400 mx-auto mb-1" />
                        <p className="text-orange-400 text-xs font-medium">Drop to assign here</p>
                      </div>
                    )}

                    <div className="flex space-x-2 border-t border-gray-700 pt-3">
                      <Button size="sm" variant="outline" className="border-blue-500/40 text-blue-400 hover:bg-blue-500/10" onClick={() => handleEdit(team)} data-testid={`edit-st-${team.id}`}><Edit className="h-3 w-3 mr-1" /> Edit</Button>
                      <Button size="sm" variant="outline" className="border-green-500/40 text-green-400 hover:bg-green-500/10" onClick={() => handlePrint(team)} data-testid={`print-st-${team.id}`}><Printer className="h-3 w-3 mr-1" /> Print</Button>
                      <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10" onClick={() => handleDelete(team.id)} data-testid={`delete-st-${team.id}`}><Trash2 className="h-3 w-3 mr-1" /> Delete</Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Assign Sidebar */}
      <div className={`fixed right-0 top-0 h-full w-80 bg-gray-900 border-l border-gray-700 shadow-2xl z-40 transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}`} data-testid="quick-assign-sidebar">
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-gray-700 bg-gray-900/95 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-white flex items-center gap-2"><UserPlus className="h-4 w-4 text-orange-500" /> Quick Assign</h3>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-white" onClick={() => setSidebarOpen(false)} data-testid="close-quick-assign-btn"><X className="h-4 w-4" /></Button>
            </div>
            <p className="text-gray-500 text-xs mb-3">Drag onto a Strike Team card to assign or reassign.</p>

            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-500" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search participants or units..."
                className="pl-8 bg-gray-800 border-gray-600 text-white text-sm h-8" data-testid="quick-assign-search" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
              <>
                {/* Participants Section - shows ALL (dimmed if already assigned) */}
                <div className="p-3 border-b border-gray-700/50">
                  <p className="text-orange-500/70 text-xs font-medium uppercase tracking-wider mb-2">
                    Participants ({filteredUnassignedParticipants.length})
                  </p>
                  {filteredUnassignedParticipants.length === 0 ? (
                    <div className="text-center py-6">
                      <Users className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-500 text-xs">{searchQuery ? 'No matches' : 'No participants in this exercise'}</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {filteredUnassignedParticipants.map(p => {
                        const rawTeam = p.strikeTeam || p.strike_team || '';
                        const isAssigned = isRealAssignment(rawTeam);
                        const currentTeam = isAssigned ? rawTeam : '';
                        const displayName = p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unnamed';
                        return (
                          <div
                            key={p.id}
                            draggable
                            onDragStart={(e) => onDragStart(e, p, 'participant')}
                            className={`flex items-center gap-2 px-2.5 py-2 rounded-md border cursor-grab active:cursor-grabbing transition-colors group ${
                              isAssigned
                                ? 'bg-gray-800/20 border-gray-700/30 opacity-50 hover:opacity-90'
                                : 'bg-gray-800/50 border-gray-700/50 hover:border-orange-500/30 hover:bg-gray-800'
                            }`}
                            data-testid={`st-draggable-participant-${p.id}`}
                          >
                            <GripVertical className={`h-3.5 w-3.5 flex-shrink-0 ${isAssigned ? 'text-gray-700' : 'text-gray-600 group-hover:text-orange-500/60'}`} />
                            <Users className="h-3.5 w-3.5 text-orange-500/40 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm truncate ${isAssigned ? 'text-gray-400' : 'text-white'}`}>{displayName}</p>
                              <p className="text-xs truncate">
                                {isAssigned ? (
                                  <span className="text-orange-300">Currently: {currentTeam}</span>
                                ) : (
                                  <span className="text-gray-500">{p.position || p.organization || 'Unassigned'}</span>
                                )}
                              </p>
                            </div>
                            {isAssigned && (
                              <button
                                className="text-gray-600 hover:text-red-400 transition-colors p-0.5 opacity-0 group-hover:opacity-100"
                                onClick={() => unassignParticipant(p.id)}
                                title="Remove from strike team"
                                data-testid={`st-sidebar-unassign-participant-${p.id}`}
                              >
                                <UserMinus className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {assigningId === p.id && <Loader2 className="h-3 w-3 animate-spin text-orange-500 flex-shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="p-3 border-b border-gray-700/50">
                  <p className="text-teal-500/70 text-xs font-medium uppercase tracking-wider mb-2">Resources ({filteredUnassignedResources.length})</p>
                  {filteredUnassignedResources.length === 0 ? (
                    <div className="text-center py-6"><Package className="h-8 w-8 text-gray-600 mx-auto mb-2" /><p className="text-gray-500 text-xs">{searchQuery ? 'No matches' : 'No resources in this exercise'}</p></div>
                  ) : (
                    <div className="space-y-1">
                      {filteredUnassignedResources.map(r => {
                        const rawTeam = r.strike_team || '';
                        const isAssigned = isRealAssignment(rawTeam);
                        return (
                          <div key={r.id} draggable onDragStart={(e) => onDragStart(e, r, 'resource')}
                            className={`flex items-center gap-2 px-2.5 py-2 rounded-md border cursor-grab active:cursor-grabbing transition-colors group ${
                              isAssigned
                                ? 'bg-gray-800/20 border-gray-700/30 opacity-50 hover:opacity-90'
                                : 'bg-gray-800/50 border-gray-700/50 hover:border-teal-500/30 hover:bg-gray-800'
                            }`}
                            data-testid={`st-draggable-resource-${r.id}`}>
                            <GripVertical className={`h-3.5 w-3.5 flex-shrink-0 ${isAssigned ? 'text-gray-700' : 'text-gray-600 group-hover:text-teal-500/60'}`} />
                            <Package className="h-3.5 w-3.5 text-teal-500/40 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm truncate ${isAssigned ? 'text-gray-400' : 'text-white'}`}>{r.unit_name || 'Unnamed'}</p>
                              <p className="text-xs truncate">
                                {isAssigned ? (
                                  <span className="text-teal-300">Currently: {rawTeam}</span>
                                ) : (
                                  <span className="text-gray-500">{r.resource_type || ''}</span>
                                )}
                              </p>
                            </div>
                            {!isAssigned && <Badge className={`text-[10px] px-1.5 py-0 ${statusBadgeClass(r.status)}`}>{r.status}</Badge>}
                            {isAssigned && (
                              <button className="text-gray-600 hover:text-red-400 transition-colors p-0.5 opacity-0 group-hover:opacity-100"
                                onClick={() => unassignResource(r.id)} data-testid={`st-sidebar-unassign-resource-${r.id}`}>
                                <UserMinus className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {assigningId === r.id && <Loader2 className="h-3 w-3 animate-spin text-teal-500 flex-shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Aircraft Section - Fixed Wing / Helicopters / Drones from ICS 220 */}
                <div className="p-3">
                  <p className="text-sky-500/70 text-xs font-medium uppercase tracking-wider mb-2">Aircraft ({filteredAircraft.length})</p>
                  {filteredAircraft.length === 0 ? (
                    <div className="text-center py-6"><Plane className="h-8 w-8 text-gray-600 mx-auto mb-2" /><p className="text-gray-500 text-xs">{searchQuery ? 'No matches' : 'No aircraft in ICS 220 for this exercise'}</p></div>
                  ) : (
                    <div className="space-y-1">
                      {filteredAircraft.map(a => {
                        const rawTeam = a.strike_team || '';
                        const isAssigned = isRealAssignment(rawTeam);
                        const key = aircraftKey(a);
                        const catLabel = a.category === 'fixed_wing' ? 'Fixed Wing' : a.category === 'helicopter' ? 'Helicopter' : 'Drone';
                        return (
                          <div key={key} draggable onDragStart={(e) => onDragStart(e, a, 'aircraft')}
                            className={`flex items-center gap-2 px-2.5 py-2 rounded-md border cursor-grab active:cursor-grabbing transition-colors group ${
                              isAssigned
                                ? 'bg-gray-800/20 border-gray-700/30 opacity-50 hover:opacity-90'
                                : 'bg-gray-800/50 border-gray-700/50 hover:border-sky-500/30 hover:bg-gray-800'
                            }`}
                            data-testid={`st-draggable-aircraft-${key}`}>
                            <GripVertical className={`h-3.5 w-3.5 flex-shrink-0 ${isAssigned ? 'text-gray-700' : 'text-gray-600 group-hover:text-sky-500/60'}`} />
                            <Plane className="h-3.5 w-3.5 text-sky-500/40 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm truncate ${isAssigned ? 'text-gray-400' : 'text-white'}`}>{a.registration || a.make_model || 'Unnamed'}</p>
                              <p className="text-xs truncate">
                                {isAssigned ? (
                                  <span className="text-sky-300">Currently: {rawTeam}</span>
                                ) : (
                                  <span className="text-gray-500">{catLabel}{a.make_model ? ` · ${a.make_model}` : ''}</span>
                                )}
                              </p>
                            </div>
                            {isAssigned && (
                              <button className="text-gray-600 hover:text-red-400 transition-colors p-0.5 opacity-0 group-hover:opacity-100"
                                onClick={() => unassignAircraft(a)} title="Remove from strike team" data-testid={`st-sidebar-unassign-aircraft-${key}`}>
                                <UserMinus className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {assigningId === key && <Loader2 className="h-3 w-3 animate-spin text-sky-500 flex-shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
          </div>

          <div className="p-3 border-t border-gray-700 bg-gray-900/95">
            <div className="flex justify-between text-xs text-gray-500">
              <span>{participants.length}p · {resources.length}r · {aircraft.length}a</span>
              <span>{assignedParticipants.length + assignedResources.length + assignedAircraft.length} assigned</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrikeTeamsModule;
